import clsx from 'clsx'
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {KTSVG} from '../../../../_gori/helpers'
import {useIntl} from 'react-intl'
import {Button, InputCheckNoFormik} from '../../../../_gori/partials/widgets'
import {AccountBranchesResponse, BranchesMenuBranch, BranchesMenuData} from '../core/_types'

export type {BranchesMenuAccount, BranchesMenuBranch, BranchesMenuData} from '../core/_types'

export const DEFAULT_BRANCHES_MENU: BranchesMenuData = {
  branches: [],
  unassignedAccounts: [],
}

export const DEFAULT_BRANCHES: BranchesMenuBranch[] = []

export const mapBranchesResponseToMenuData = (
  response?: AccountBranchesResponse
): BranchesMenuData => ({
  branches: (response?.branches ?? []).map((branch) => ({
    id: branch.id,
    name: branch.name,
    checked: true,
    expanded: false,
    accounts: branch.accounts.map((account) => ({
      id: account.id,
      name: account.name,
      checked: true,
    })),
  })),
  unassignedAccounts: (response?.unassigned_accounts ?? []).map((account) => ({
    id: account.id,
    name: account.name,
    checked: true,
  })),
})

/** @deprecated Use mapBranchesResponseToMenuData */
export const mapBranchesResponseToMenuBranches = (
  response?: AccountBranchesResponse
): BranchesMenuBranch[] => mapBranchesResponseToMenuData(response).branches

export const getSelectedAccountIds = (menuData: BranchesMenuData) => [
  ...menuData.branches.flatMap((branch) =>
    branch.accounts.filter((account) => account.checked).map((account) => account.id)
  ),
  ...menuData.unassignedAccounts.filter((account) => account.checked).map((account) => account.id),
]

type Props = {
  menuData?: BranchesMenuData
  /** @deprecated Use menuData */
  branches?: BranchesMenuData
  onMenuDataChange?: (menuData: BranchesMenuData) => void
  /** @deprecated Use onMenuDataChange */
  onBranchesChange?: (menuData: BranchesMenuData) => void
  showTitle?: boolean
  className?: string
}

const BranchesMenu: React.FC<Props> = ({
  menuData: controlledMenuData,
  branches: legacyBranches,
  onMenuDataChange,
  onBranchesChange,
  showTitle = true,
  className,
}) => {
  const [search, setSearch] = useState('')
  const [isSearchFocused, setIsSearchFocused] = useState(false)
  const [unassignedExpanded, setUnassignedExpanded] = useState(false)
  const searchTextareaRef = useRef<HTMLTextAreaElement>(null)
  const [internalMenuData, setInternalMenuData] = useState<BranchesMenuData>(DEFAULT_BRANCHES_MENU)
  const intl = useIntl()

  const syncSearchTextareaHeight = useCallback(() => {
    if (!searchTextareaRef.current) return
    const target = searchTextareaRef.current
    target.style.height = 'auto'
    target.style.height = `${Math.max(44, Math.min(target.scrollHeight, 200))}px`
  }, [])

  useEffect(() => {
    syncSearchTextareaHeight()
  }, [search, syncSearchTextareaHeight])

  const menuData = controlledMenuData ?? legacyBranches ?? internalMenuData
  const {branches, unassignedAccounts} = menuData
  const notifyChange = onMenuDataChange ?? onBranchesChange

  const setMenuDataAndNotify = (nextMenuData: BranchesMenuData) => {
    if (notifyChange) {
      notifyChange(nextMenuData)
      return
    }
    setInternalMenuData(nextMenuData)
  }

  const filteredBranches = useMemo(() => {
    const keyword = search.trim().toLowerCase()
    if (!keyword) return branches

    return branches
      .map((branch) => {
        const branchMatched = branch.name.toLowerCase().includes(keyword)
        const matchedAccounts = branch.accounts.filter((account) =>
          account.name.toLowerCase().includes(keyword)
        )

        if (!branchMatched && matchedAccounts.length === 0) {
          return null
        }

        return {
          ...branch,
          expanded: branch.expanded || branchMatched || matchedAccounts.length > 0,
          accounts: branchMatched ? branch.accounts : matchedAccounts,
        }
      })
      .filter(Boolean) as BranchesMenuBranch[]
  }, [branches, search])

  const filteredUnassignedAccounts = useMemo(() => {
    const keyword = search.trim().toLowerCase()
    if (!keyword) return unassignedAccounts

    return unassignedAccounts.filter((account) => account.name?.toLowerCase().includes(keyword))
  }, [unassignedAccounts, search])

  const totalFilteredAccounts = useMemo(
    () => filteredBranches.reduce((total, branch) => total + branch.accounts.length, 0),
    [filteredBranches]
  )

  const totalBranches = branches.length
  const allBranchesSelected = useMemo(
    () => totalBranches > 0 && branches.every((branch) => branch.checked),
    [branches, totalBranches]
  )

  const hasSelectedUnassignedAccounts = unassignedAccounts.some((account) => account.checked)
  const showBranchlessSection =
    unassignedAccounts.length > 0 && (!search.trim() || filteredUnassignedAccounts.length > 0)
  const isUnassignedExpanded =
    unassignedExpanded || (!!search.trim() && filteredUnassignedAccounts.length > 0)

  const toggleAllBranches = () => {
    const nextChecked = !allBranchesSelected
    setMenuDataAndNotify({
      ...menuData,
      branches: branches.map((branch) => ({
        ...branch,
        checked: nextChecked,
        accounts: branch.accounts.map((account) => ({...account, checked: nextChecked})),
      })),
    })
  }

  const toggleBranchlessSection = () => {
    const nextChecked = !hasSelectedUnassignedAccounts
    setMenuDataAndNotify({
      ...menuData,
      unassignedAccounts: unassignedAccounts.map((account) => ({
        ...account,
        checked: nextChecked,
      })),
    })
  }

  const toggleUnassignedExpand = () => {
    setUnassignedExpanded((current) => !current)
  }

  const toggleBranch = (branchId: string | number) => {
    setMenuDataAndNotify({
      ...menuData,
      branches: branches.map((branch) => {
        if (branch.id !== branchId) return branch
        const hasSelectedAccounts = branch.accounts.some((account) => account.checked)
        const nextChecked = !hasSelectedAccounts
        return {
          ...branch,
          checked: nextChecked,
          accounts: branch.accounts.map((account) => ({...account, checked: nextChecked})),
        }
      }),
    })
  }

  const toggleBranchExpand = (branchId: string | number) => {
    const nextBranches = branches.map((branch) =>
      branch.id === branchId ? {...branch, expanded: !branch.expanded} : branch
    )

    if (notifyChange) {
      notifyChange({...menuData, branches: nextBranches})
      return
    }
    setInternalMenuData((current) => ({...current, branches: nextBranches}))
  }

  const toggleAccount = (branchId: string | number, accountId: string | number) => {
    setMenuDataAndNotify({
      ...menuData,
      branches: branches.map((branch) => {
        if (branch.id !== branchId) return branch

        const nextAccounts = branch.accounts.map((account) =>
          account.id === accountId ? {...account, checked: !account.checked} : account
        )

        return {
          ...branch,
          checked: nextAccounts.length > 0 && nextAccounts.every((account) => account.checked),
          accounts: nextAccounts,
        }
      }),
    })
  }

  const toggleUnassignedAccount = (accountId: string | number) => {
    setMenuDataAndNotify({
      ...menuData,
      unassignedAccounts: unassignedAccounts.map((account) =>
        account.id === accountId ? {...account, checked: !account.checked} : account
      ),
    })
  }

  return (
    <div className={clsx('branches-menu', className)}>
      {showTitle && (
        <h3 className='branches-menu__title'>{intl.formatMessage({id: 'ACCOUNTS'})}</h3>
      )}

      <div className='branches-menu__search'>
        <div
          className={clsx('input-search-container position-relative mw-100', {
            active: isSearchFocused || search,
          })}
        >
          <KTSVG
            path='/media/gori/magnify.svg'
            className='svg-icon-1 position-absolute top-0 ms-4 mt-3'
          />
          <textarea
            ref={searchTextareaRef}
            autoComplete='off'
            className='form-control form-control-solid ps-14 pe-6 input-search-textarea input-search-textarea-fixed border-0'
            placeholder={intl.formatMessage({id: 'SEARCH_ACCOUNTS'})}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
              }
            }}
            onFocus={() => setIsSearchFocused(true)}
            onBlur={() => setIsSearchFocused(false)}
            value={search}
            rows={1}
            onInput={syncSearchTextareaHeight}
          />
          {search && (
            <Button
              event={() => {
                setSearch('')
                syncSearchTextareaHeight()
              }}
              classNameDiv='d-flex justify-content-center align-items-center'
              className='text-hover-dark border-0 bg-transparent rounded-circle py-1 position-absolute top-0 end-0 me-4 mt-2'
            >
              <KTSVG
                path='/media/gori/orders/delete.svg'
                className='opacity-50 m-0'
                svgClassName='mh-10px'
              />
            </Button>
          )}
        </div>
      </div>

      <div
        className={clsx('branches-menu__row branches-menu__row--all', {
          'is-active': allBranchesSelected,
        })}
      >
        <div className='branches-menu__item'>
          <InputCheckNoFormik
            checked={allBranchesSelected}
            onChange={toggleAllBranches}
            label={`${intl.formatMessage({id: 'ALL_BRANCHES'})} (${totalFilteredAccounts})`}
            labelClassName='branches-menu__text'
            className='branches-menu__checkbox'
          />
        </div>
      </div>

      <div className='branches-menu__list'>
        {filteredBranches.map((branch) => {
          const hasSelectedAccounts = branch.accounts.some((account) => account.checked)

          return (
            <div key={branch.id}>
              <div
                className={clsx('branches-menu__row', {
                  'is-active': hasSelectedAccounts && !allBranchesSelected,
                })}
              >
                <div className='branches-menu__item'>
                  <InputCheckNoFormik
                    type='checkbox'
                    checked={hasSelectedAccounts}
                    onChange={() => toggleBranch(branch.id)}
                    label={branch.name}
                    labelClassName='branches-menu__text'
                    className='branches-menu__checkbox'
                  />
                </div>

                <button
                  type='button'
                  className='branches-menu__toggle'
                  onClick={() => toggleBranchExpand(branch.id)}
                  aria-expanded={branch.expanded}
                >
                  <KTSVG
                    path={
                      branch.expanded
                        ? '/media/gori/orders/minus.svg'
                        : '/media/gori/orders/plus.svg'
                    }
                    className='svg-icon-2 branches-menu__toggle-icon'
                  />
                </button>
              </div>

              {branch.expanded && (
                <div className='branches-menu__accounts'>
                  {branch.accounts.map((account) => (
                    <div
                      key={account.id}
                      className='branches-menu__item branches-menu__item--child'
                    >
                      <InputCheckNoFormik
                        type='checkbox'
                        checked={!!account.checked}
                        onChange={() => toggleAccount(branch.id, account.id)}
                        label={account.name}
                        labelClassName='branches-menu__text'
                        className='branches-menu__checkbox'
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {showBranchlessSection && (
        <div className='branches-menu__section branches-menu__section--branchless'>
          <div
            className={clsx('branches-menu__row branches-menu__row--section', {
              'is-active': hasSelectedUnassignedAccounts && !allBranchesSelected,
            })}
          >
            <div className='branches-menu__item'>
              <InputCheckNoFormik
                type='checkbox'
                checked={hasSelectedUnassignedAccounts}
                onChange={toggleBranchlessSection}
                label={`${intl.formatMessage({id: 'NO_BRANCH'})} (${
                  filteredUnassignedAccounts.length
                })`}
                labelClassName='branches-menu__text'
                className='branches-menu__checkbox'
              />
            </div>

            <button
              type='button'
              className='branches-menu__toggle'
              onClick={toggleUnassignedExpand}
              aria-expanded={isUnassignedExpanded}
            >
              <KTSVG
                path={
                  isUnassignedExpanded
                    ? '/media/gori/orders/minus.svg'
                    : '/media/gori/orders/plus.svg'
                }
                className='svg-icon-2 branches-menu__toggle-icon'
              />
            </button>
          </div>

          {isUnassignedExpanded && (
            <div className='branches-menu__accounts'>
              {filteredUnassignedAccounts.map((account) => (
                <div key={account.id} className='branches-menu__item branches-menu__item--child'>
                  <InputCheckNoFormik
                    type='checkbox'
                    checked={!!account.checked}
                    onChange={() => toggleUnassignedAccount(account.id)}
                    label={account.name}
                    labelClassName='branches-menu__text'
                    className='branches-menu__checkbox'
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export {BranchesMenu}
